//#region Imports
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
//#endregion

//#region Internal Imports
import { CustomField } from 'app/core/model/custom-fields/custom-field.model';
import { CustomFieldCondition } from 'app/core/model/custom-fields/custom-field-condition.model';
import { CustomFieldSchema } from 'app/core/model/custom-fields/custom-field-schema.model';
import { ModuleScope } from 'app/core/model/custom-fields/module-assignment.model';
import { CustomFieldsService } from 'app/modules/settings/component/service/custom-fields.service';
//#endregion

@Component({
  selector: 'app-custom-field-renderer',
  templateUrl: './custom-field-renderer.component.html',
  styleUrl: './custom-field-renderer.component.scss',
})
export class CustomFieldRendererComponent implements OnInit, OnChanges {
  // ---------------------------------------------------------------------------
  // Inputs / Outputs
  // ---------------------------------------------------------------------------

  @Input() module: ModuleScope;
  @Input() recordId: number = null;
  @Input() readonly: boolean = false;
  @Input() isApprovalMode: boolean = false;

  @Output() valuesChange = new EventEmitter<Record<string, any>>();

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  schema: CustomFieldSchema = new CustomFieldSchema();
  form: FormGroup;
  loading = true;

  /** Tracks which fields are currently visible (keyed by internal_name) */
  fieldVisibility: Record<string, boolean> = {};

  /**
   * Cached dropdown / multi-select options keyed by internal_name.
   * Built once per schema load so the template never creates fresh array refs
   * on each change-detection cycle (which makes p-dropdown drop its selection display).
   */
  fieldOptionsCache: Record<string, { label: string; value: string }[]> = {};

  constructor(
    private fb: FormBuilder,
    private customFieldsService: CustomFieldsService,
  ) {}

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    // Intentionally empty — loadSchema() is driven entirely by ngOnChanges.
    // ngOnChanges fires before ngOnInit on component creation (with all initial
    // input values), so it handles both the first-time load and subsequent
    // input changes (e.g. switching from create → edit with a new recordId).
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['module'] || changes['recordId']) && this.module) {
      this.loadSchema();
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  getValues(): Record<string, any> {
    if (!this.form) return {};
    const raw = this.form.getRawValue();
    const deletedFieldNames = new Set(
      this.schema.groups.flatMap((g) => g.fields || [])
        .filter((f) => !f.active)
        .map((f) => f.internal_name),
    );
    // Exclude hidden fields and deleted fields — their values should not be submitted
    const visible: Record<string, any> = {};
    for (const key of Object.keys(raw)) {
      if (this.fieldVisibility[key] !== false && !deletedFieldNames.has(key)) {
        visible[key] = raw[key];
      }
    }
    return visible;
  }

  isValid(): boolean {
    if (!this.form) return true;
    // Only touch visible fields so hidden-field errors don't block submission
    const allFields = this.schema.groups.flatMap((g) => g.fields || []);
    for (const field of allFields) {
      if (this.fieldVisibility[field.internal_name] !== false) {
        this.form.get(field.internal_name)?.markAsTouched();
      }
    }
    // Form is valid when all visible enabled controls are valid
    return allFields.every((field) => {
      if (this.fieldVisibility[field.internal_name] === false) return true;
      const ctrl = this.form.get(field.internal_name);
      return !ctrl || ctrl.valid;
    });
  }

  /**
   * Returns labels of visible, active, required fields with no value.
   * Unlike isValid(), this works even when the form is in readonly mode —
   * Angular disables controls in that mode and exempts them from form-level
   * validity, so isValid() would falsely return true. This inspects raw values
   * directly. Used by views that show CFs read-only but still need to gate
   * an action (e.g. report submit) on required fields being filled.
   */
  getMissingRequiredFieldLabels(): string[] {
    if (!this.form) return [];
    const raw = this.form.getRawValue();
    const missing: string[] = [];
    for (const group of this.schema.groups || []) {
      for (const field of group.fields || []) {
        if (!field.active || !field.is_required) continue;
        if (this.fieldVisibility[field.internal_name] === false) continue;
        const value = raw[field.internal_name];
        const empty =
          value === null ||
          value === undefined ||
          value === '' ||
          (field.field_type === 'multi_select' && Array.isArray(value) && value.length === 0) ||
          (field.field_type === 'switch' && value !== true);
        if (empty) missing.push(field.label);
      }
    }
    return missing;
  }

  hasFields(): boolean {
    return this.schema.groups.some((g) => g.fields?.some((f) => f.active || this.form?.get(f.internal_name)));
  }

  isVisible(field: CustomField): boolean {
    // Inactive (deleted) fields are only renderable when this record has a saved value
    // for them — otherwise they have no form control and shouldn't contribute to group visibility.
    if (!field.active && !this.form?.get(field.internal_name)) return false;
    return this.fieldVisibility[field.internal_name] !== false;
  }

  isDeleted(field: CustomField): boolean {
    return !field.active;
  }

  isGroupVisible(group: any): boolean {
    return (group.fields || []).some((f: CustomField) =>
      (f.active || this.isDeleted(f)) && this.isVisible(f)
    );
  }

  // ---------------------------------------------------------------------------
  // Helpers used in template
  // ---------------------------------------------------------------------------

  getOptionItems(field: CustomField): { label: string; value: string }[] {
    const cached = this.fieldOptionsCache[field.internal_name];
    if (cached) return cached;
    const built = (field.options || []).map((o) => ({ label: o.label, value: String(o.value) }));
    this.fieldOptionsCache[field.internal_name] = built;
    return built;
  }

  getError(field: CustomField): string | null {
    const ctrl = this.form?.get(field.internal_name);
    if (!ctrl || !ctrl.invalid || !ctrl.touched) return null;
    const e = ctrl.errors;
    if (e['required']) {
      return field.field_type === 'switch'
        ? `${field.label} must be enabled.`
        : `${field.label} is required.`;
    }
    if (e['pattern']) return field.regex_error_message || `${field.label} format is invalid.`;
    if (e['minlength']) return `Minimum ${e['minlength'].requiredLength} characters required.`;
    if (e['maxlength']) return `Maximum ${e['maxlength'].requiredLength} characters allowed.`;
    if (e['min']) return `Minimum value is ${e['min'].min}.`;
    if (e['max']) return `Maximum value is ${e['max'].max}.`;
    return null;
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private loadSchema(): void {
    this.loading = true;
    const values$ = this.recordId
      ? this.customFieldsService.getValues(this.module, this.recordId)
      : of({} as Record<string, any>);

    forkJoin([this.customFieldsService.getSchema(this.module, this.recordId), values$]).subscribe({
      next: ([schema, values]) => {
        this.schema = schema;
        this.fieldOptionsCache = {};
        this.buildForm(values);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private buildForm(existingValues: Record<string, any>): void {
    const controls: Record<string, any> = {};
    // Include inactive (deleted) fields only when viewing an existing record that has values for them
    const allFields = this.schema.groups.flatMap((g) => g.fields || []).filter((f) => {
      if (f.active) return true;
      // Show deleted fields only when editing an existing record and a saved value exists
      return this.recordId && existingValues?.hasOwnProperty(f.internal_name);
    });

    for (const field of allFields) {
      // Deleted fields get no validators — they are read-only historical data
      const validators: ValidatorFn[] = [];

      if (field.active) {
        if (field.is_required) {
          // Switch fields require an explicit "true" value when marked required
          validators.push(field.field_type === 'switch' ? Validators.requiredTrue : Validators.required);
        }

        if (field.field_type === 'number') {
          if (field.min_value !== null && field.min_value !== '') {
            validators.push(Validators.min(Number(field.min_value)));
          }
          if (field.max_value !== null && field.max_value !== '') {
            validators.push(Validators.max(Number(field.max_value)));
          }
        }

        if ((field.field_type === 'text' || field.field_type === 'textarea') && field.regex_pattern) {
          validators.push(Validators.pattern(field.regex_pattern));
        }

        if (field.field_type === 'text' || field.field_type === 'textarea') {
          if (field.min_length !== null) validators.push(Validators.minLength(field.min_length));
          if (field.max_length !== null) validators.push(Validators.maxLength(field.max_length));
        }

        if (field.field_type === 'url') {
          validators.push(Validators.pattern(/^https?:\/\/.+/));
        }
      }

      let initialValue = existingValues?.[field.internal_name] ?? field.default_value ?? null;

      if (field.field_type === 'multi_select') {
        // Option values are always strings — coerce each element so p-multiSelect's strict
        // equality match against optionValue succeeds even if the backend returns numbers.
        const arr = initialValue ? (Array.isArray(initialValue) ? initialValue : [initialValue]) : [];
        initialValue = arr.map((v) => v === null || v === undefined ? v : String(v));
      } else if (field.field_type === 'dropdown') {
        // Same reason as multi_select — option values are strings, form control must match.
        if (initialValue !== null && initialValue !== undefined && initialValue !== '') {
          initialValue = String(initialValue);
        } else {
          initialValue = null;
        }
      } else if (field.field_type === 'switch') {
        initialValue = initialValue === true || initialValue === 'true';
      } else if (field.field_type === 'date' && initialValue) {
        initialValue = new Date(initialValue);
      } else if (field.field_type === 'number' && initialValue !== null && initialValue !== '') {
        initialValue = Number(initialValue);
      }

      controls[field.internal_name] = [initialValue, validators];
    }

    this.form = this.fb.group(controls);

    // Disable deleted fields — always read-only
    for (const field of allFields) {
      if (!field.active) {
        this.form.get(field.internal_name)?.disable();
      }
    }

    if (this.readonly) {
      this.form.disable();
    } else if (this.isApprovalMode) {
      for (const field of allFields) {
        if (!field.is_editable_in_approval) {
          this.form.get(field.internal_name)?.disable();
        }
      }
    }

    // Run initial condition evaluation before any user interaction
    this.evaluateConditions(this.form.getRawValue());

    // Re-evaluate on every value change
    this.form.valueChanges.subscribe(() => {
      this.evaluateConditions(this.form.getRawValue());
      this.valuesChange.emit(this.getValues());
    });
  }

  // ---------------------------------------------------------------------------
  // Condition evaluation
  // ---------------------------------------------------------------------------

  private evaluateConditions(values: Record<string, any>): void {
    const conditions = this.schema.conditions || [];

    // Deleted fields are always visible (read-only historical data, not affected by conditions)
    const deletedFields = this.schema.groups.flatMap((g) => g.fields || [])
      .filter((f) => !f.active && this.form?.get(f.internal_name));
    for (const field of deletedFields) {
      this.fieldVisibility[field.internal_name] = true;
    }

    const allFields = this.schema.groups.flatMap((g) => g.fields || []).filter((f) => f.active);

    if (conditions.length === 0) {
      // No conditions — all active fields visible
      for (const field of allFields) {
        this.fieldVisibility[field.internal_name] = true;
      }
      return;
    }

    for (const field of allFields) {
      const fieldConditions = conditions.filter((c) => c.target_field === field.internal_name);

      if (fieldConditions.length === 0) {
        this.fieldVisibility[field.internal_name] = true;
        continue;
      }

      const showConditions = fieldConditions.filter((c) => c.action === 'show');
      const hideConditions = fieldConditions.filter((c) => c.action === 'hide');

      const anyShowMet = showConditions.some((c) => this.isConditionMet(c, values));
      const anyHideMet = hideConditions.some((c) => this.isConditionMet(c, values));

      let visible: boolean;
      if (showConditions.length > 0 && hideConditions.length === 0) {
        visible = anyShowMet;
      } else if (hideConditions.length > 0 && showConditions.length === 0) {
        visible = !anyHideMet;
      } else {
        // Mixed: show condition must be met AND no hide condition must fire
        visible = anyShowMet && !anyHideMet;
      }

      this.fieldVisibility[field.internal_name] = visible;

      // Clear value when field becomes hidden
      if (!visible) {
        const ctrl = this.form.get(field.internal_name);
        if (ctrl && ctrl.value !== null && ctrl.value !== '' &&
            !(Array.isArray(ctrl.value) && ctrl.value.length === 0)) {
          ctrl.setValue(
            field.field_type === 'multi_select' ? [] : null,
            { emitEvent: false },
          );
        }
      }
    }
  }

  private isConditionMet(condition: CustomFieldCondition, values: Record<string, any>): boolean {
    const triggerValue = values[condition.trigger_field];
    if (triggerValue === null || triggerValue === undefined || triggerValue === '') return false;
    if (Array.isArray(triggerValue)) {
      // multi_select: condition met when trigger_value is in the selection
      return triggerValue.map(String).includes(condition.trigger_value);
    }
    // switch: compare as lowercase string; dropdown: direct comparison
    return String(triggerValue).toLowerCase() === condition.trigger_value.toLowerCase();
  }
}
